import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { PLANS } from './balder-cart-invariants.js';

/**
 * <balder-subscription-toggle> — one-click switch between one-time and
 * subscription on a cart line. Reads the dog size from the line's hidden
 * _size property (stamped by the bundle picker) and picks the size's 1-pack
 * or 3-pack Loop plan based on quantity. Toggling off removes the plan.
 */

class BalderSubscriptionToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = async (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('button')) return;
    if (this.hasAttribute('busy')) return;

    const { key, size } = this.dataset;
    const quantity = Number(this.dataset.quantity);
    const active = this.hasAttribute('data-active');
    const plan = PLANS[size]?.[quantity >= 3 ? 3 : 1];
    if (!key || !quantity || (!active && !plan)) return;

    this.setAttribute('busy', '');
    try {
      const body = JSON.stringify({ id: key, quantity, selling_plan: active ? null : plan });
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      if (!response.ok) throw new Error(`toggle change failed (${response.status})`);
      const cart = await response.json();
      // No sections payload — cart components re-render themselves, which
      // replaces this element with the new state.
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'balder-subscription-toggle', {
          source: 'balder-subscription-toggle',
          itemCount: cart.item_count,
        })
      );
    } catch (error) {
      console.error('[subscription-toggle]', error);
      this.removeAttribute('busy');
    }
  };
}

if (!customElements.get('balder-subscription-toggle')) {
  customElements.define('balder-subscription-toggle', BalderSubscriptionToggle);
}

// /cart redirects to the home page with ?cart=open (layout/theme.liquid);
// open the drawer and drop the param so a reload doesn't reopen it.
const params = new URLSearchParams(location.search);
if (params.get('cart') === 'open') {
  params.delete('cart');
  const query = params.toString();
  history.replaceState(history.state, '', `${location.pathname}${query ? `?${query}` : ''}${location.hash}`);
  customElements.whenDefined('cart-drawer-component').then(() => {
    document.querySelector('cart-drawer-component')?.open();
  });
}

// Back/Forward can show a stored copy of the page (bfcache, or the HTTP cache)
// whose drawer and bubble predate a cart change, e.g. bundle picker → checkout
// → Back. Only on those navigations: check the real cart and re-render the
// drawer + bubble if the count differs. Normal page loads do nothing.
async function syncStaleCart() {
  const drawerItems = document.querySelector('cart-drawer-component cart-items-component');
  if (!(drawerItems instanceof HTMLElement) || !drawerItems.dataset.sectionId) return;
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
    if (!response.ok) return;
    const cart = await response.json();
    const shown = Number(drawerItems.querySelector('[ref="cartItemCount"]')?.textContent.trim() || 0);
    if (cart.item_count === shown) return;
    document.querySelector('cart-icon')?.renderCartBubble?.(cart.item_count, false, false);
    await sectionRenderer.renderSection(drawerItems.dataset.sectionId, { cache: false });
  } catch (error) {
    console.error('[cart-sync]', error);
  }
}

window.addEventListener('pageshow', (event) => {
  const navigation = performance.getEntriesByType('navigation')[0];
  if (event.persisted || navigation?.type === 'back_forward') syncStaleCart();
});
